import { forwardRef, useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import {
  CalendarIcon,
  MapPinIcon,
  ArrowRightIcon,
  ClockIcon,
  ShareIcon,
} from "@heroicons/react/24/solid";
import { sendEventEmail } from "../../services/firestore/fireSideChatService";

import Event_1 from "../../assests/images/horizontal_1.jpg";
import Event_2 from "../../assests/images/horizontal_2.jpg";
import Event_3 from "../../assests/images/horizontal_3.jpeg";
import { useParams } from "react-router-dom";

const events = [
  {
    slug: "ui-ux-design-bootcamp",
    status: "LIVE NOW",
    statusColor: "bg-red-600",
    title: "UI/UX Design Bootcamp",
    desc: "Learn the essentials of UI/UX from top industry experts.",
    date: "25 Nov 2025 • 2:00 PM",
    venue: "Singapore Expo Hall 3",
    img: Event_1,
    timeline: [
      { time: "2:00 PM", activity: "Registration" },
      { time: "2:30 PM", activity: "Workshop Start" },
      { time: "5:00 PM", activity: "Q&A" },
    ],
  },
  {
    slug: "frontend-react-masterclass",
    status: "LIMITED SEATS",
    statusColor: "bg-yellow-500",
    title: "Frontend React Masterclass",
    desc: "Deep dive into real-world React patterns and architecture.",
    date: "30 Nov 2025 • 11:00 AM",
    venue: "Suntec Convention Centre",
    img: Event_2,
    timeline: [
      { time: "11:00 AM", activity: "Registration" },
      { time: "11:30 AM", activity: "Masterclass Start" },
      { time: "2:00 PM", activity: "Q&A & Networking" },
    ],
  },
  {
    slug: "career-mentorship-meetup",
    status: "LIMITED SEATS",
    statusColor: "bg-yellow-500",
    title: "Career Mentorship Meetup",
    desc: "Connect with professionals and build your roadmap.",
    date: "10 Dec 2025 • 4:00 PM",
    venue: "Marina Bay Sands",
    img: Event_3,
    timeline: [
      { time: "4:00 PM", activity: "Welcome & Networking" },
      { time: "4:30 PM", activity: "Mentorship Talks" },
      { time: "6:00 PM", activity: "Q&A Session" },
    ],
  },
];

const ThirdSection = forwardRef((props, forwardedRef) => {
  const localRef = useRef(null);
  const [inViewRef, inView] = useInView({ triggerOnce: true, threshold: 0.2 });

  // Merge forwardedRef with localRef
  useEffect(() => {
    if (forwardedRef) {
      if (typeof forwardedRef === "function") {
        forwardedRef(localRef.current);
      } else {
        forwardedRef.current = localRef.current;
      }
    }
  }, [forwardedRef]);

  const [openModal, setOpenModal] = useState(false);
  const [timelineOpen, setTimelineOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [email, setEmail] = useState("");
  const [thankYou, setThankYou] = useState(false);
  const [loading, setLoading] = useState(false);
    const { slug } = useParams();


  const cardVariants = {
    hidden: { opacity: 0, x: 80 },
    visible: (i) => ({
      opacity: 1,
      x: 0,
      transition: { type: "spring", stiffness: 120, damping: 20, delay: i * 0.1 },
    }),
  };

  const handleRegister = (event) => {
    setSelectedEvent(event);
    setOpenModal(true);
    setThankYou(false);
    setEmail("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedEvent || !email) return;

    setLoading(true);

    try {
      await sendEventEmail({
        type: "register",
        email,
        title: selectedEvent.title,
        eventDate: selectedEvent.date,
        eventLocation: selectedEvent.venue,
      });
      setThankYou(true);
    } catch (error) {
      console.error(error);
      alert("Oops! Something went wrong while sending the email.");
    } finally {
      setLoading(false);
    }
  };

  const handleShare = (event) => {
    const shareURL = `https://www.nextloop.sg/YES/events/${event.slug}.html`;
    navigator.clipboard.writeText(shareURL);
    alert("Event link copied!");
  };

  useEffect(() => {
    if (slug) {
      const event = events.find(e => e.slug === slug);
      if (event) {
        setSelectedEvent(event); // open modal
        setOpenModal(true);
      }
    }
  }, [slug]);

  return (
    <section
      ref={(node) => { localRef.current = node; inViewRef(node); }}
      className="w-full text-white py-24 px-6 lg:px-20 overflow-hidden"
    >
      {/* Header */}
      <div className="text-center mb-16">
        <h2 className="text-4xl lg:text-5xl font-semibold">Upcoming & Open Events</h2>
      </div>

      {/* Event List */}
      <div className="relative w-full overflow-x-auto overflow-y-hidden no-scrollbar h-[590px] sm:h-[590px] lg:h-[560px]">
        <div className="inline-flex gap-8 min-w-max">
          {events.map((event, i) => (
            <motion.div
              key={i}
              custom={i}
              initial="hidden"
              animate={inView ? "visible" : "hidden"}
              variants={cardVariants}
              whileHover={{ scale: 1.03 }}
              className="w-[300px] lg:w-[350px] bg-[#1A1A1A] shadow-[0_0_25px_rgba(0,0,0,0.4)] rounded-3xl p-6 flex flex-col gap-4 transition-all duration-300"
            >
              <div className="relative w-full h-48 rounded-2xl overflow-hidden">
                <img
                  src={event.img}
                  alt={event.title}
                  className="w-full h-full object-cover hover:scale-110 transition-all duration-500"
                />
                <button
                  onClick={() => { setSelectedEvent(event); setTimelineOpen(true); }}
                  className="absolute top-2 right-2 bg-black/50 p-1 rounded-full"
                  title="View Timeline"
                >
                  <ClockIcon className="w-5 h-5 text-white" />
                </button>
              </div>

              <span className={`px-3 py-1 rounded-xl text-xs font-bold ${event.statusColor}`}>{event.status}</span>

              <div className="flex items-center gap-2">
                <h3 className="text-2xl font-semibold">{event.title}</h3>
                <button onClick={() => handleShare(event)}>
                  <ShareIcon className="w-5 h-5 text-gray-400 hover:text-green-500 transition" />
                </button>
              </div>

              <p className="text-gray-400 text-sm">{event.desc}</p>

              <div className="mt-2 space-y-1 text-gray-300 text-sm">
                <div className="flex items-center gap-2">
                  <CalendarIcon className="w-4 h-4 text-[#3EB56B]" />
                  {event.date}
                </div>
                <div className="flex items-center gap-2">
                  <MapPinIcon className="w-4 h-4 text-[#3EB56B]" />
                  {event.venue}
                </div>
              </div>

              <button
                onClick={() => handleRegister(event)}
                className="mt-4 w-full bg-[#3EB56B] hover:bg-[#32a15f] text-white py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all duration-300"
              >
                Register Now <ArrowRightIcon className="w-4 h-4" />
              </button>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Registration Modal */}
      {openModal && selectedEvent && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-[#1A1A1A] rounded-2xl p-8 w-[90%] max-w-md">
            {!thankYou ? (
              <>
                <h3 className="text-xl font-semibold mb-4">Register for {selectedEvent.title}</h3>
                <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                  <input
                    type="email"
                    placeholder="Enter your email"
                    className="p-3 rounded-lg bg-[#121212] border border-gray-700 text-white focus:outline-none"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                  <button
                    type="submit"
                    disabled={loading}
                    className={`bg-[#3EB56B] text-white py-3 rounded-xl font-semibold transition-all
                      ${loading ? "bg-gray-600 cursor-not-allowed" : "hover:bg-[#32a15f]"}`}
                  >
                    {loading ? (
                      <div className="flex items-center gap-2 justify-center">
                        <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                        Sending...
                      </div>
                    ) : (
                      "Submit"
                    )}
                  </button>
                </form>
                <button
                  onClick={() => setOpenModal(false)}
                  className="mt-4 text-gray-400 text-sm underline"
                >
                  Cancel
                </button>
              </>
            ) : (
              <div className="text-center">
                <h3 className="text-xl font-semibold mb-2">Thank you!</h3>
                <p>We will send more details to {email}</p>
                <button
                  onClick={() => setOpenModal(false)}
                  className="mt-4 bg-[#3EB56B] hover:bg-[#32a15f] text-white py-2 px-6 rounded-xl font-semibold transition-all"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Timeline Modal */}
      {timelineOpen && selectedEvent && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-[#1A1A1A] rounded-2xl p-8 w-[90%] max-w-md overflow-y-auto max-h-[80vh]">
            <h3 className="text-xl font-semibold mb-6">{selectedEvent.title} Timeline</h3>
            <ul className="relative border-l border-gray-600 ml-4">
              {selectedEvent.timeline.map((item, index) => (
                <li key={index} className="mb-6 ml-4 relative">
                  <span className="absolute w-3 h-3 bg-[#2F7C57] rounded-full -left-1.5 border border-gray-800"></span>
                  <p className="text-gray-300 font-medium pl-[10px]">{item.time}</p>
                  <p className="text-gray-400 pl-[10px]">{item.activity}</p>
                </li>
              ))}
            </ul>
            <button
              onClick={() => setTimelineOpen(false)}
              className="mt-6 bg-[#3EB56B] hover:bg-[#32a15f] text-white py-2 px-6 rounded-xl font-semibold transition-all"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </section>
  );
});

export default ThirdSection;
